import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  Button,
  Flex,
  Select,
  Form,
  Switch,
  Modal,
  Input,
  Tooltip,
  message,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CloseOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import "./styles.css";
import {
  inviteesApi,
  meetingRoomApis,
  roomBookingApi,
  staticApis,
} from "../../api";
import dayjs from "dayjs";
import toast from "react-hot-toast";
import DeletePopup from "./DeleteMeetingPopup";
import MeetingInformationPopup from "./MeetingInformationPopup";
import ClockTimePicker from "./ClockTimePicker";
import { employeeId, GetRole } from "../../userRole";

const CalendarPopup = ({ event, handlePopupCancel, onOpenRelease }) => {
  const [confirmationModal, setConfirmationModal] = useState(null);
  const [invitees, setInvitees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [form] = Form.useForm();
  const watchedStart = Form.useWatch("start", form);
  const isAdmin = GetRole() === "admin";
  const canRelease =
    isAdmin &&
    event?.rtId &&
    event?.meetingRoomName !== "Virtual Meeting" &&
    !dayjs().isAfter(dayjs(event.end));

  const isPastMeeting = !dayjs(event.start).isAfter(dayjs());

  const openReleaseModal = () =>
    onOpenRelease?.({
      rtId: event.rtId,
      rbId: event.rbId,
      originalBooker: event.createdBy,
      originalBookerName: event.createdByName || event.createdBy,
    });

  const [copied, setCopied] = useState(false);

  useEffect(() => {
    inviteesApi
      .getInvitees({ rtId: event.rtId, empCode: event.empCode })
      .then((res) => {
        let formatInvitees = [];
        event.empCode === event.createdBy
          ? (formatInvitees = res?.data
              ?.filter((invitee) => invitee.empCode !== event.empCode)
              .map((invitee, ind) => {
                return {
                  value: invitee.empCode,
                  label: `${invitee.name}`,
                };
              }))
          : (formatInvitees = res?.data.map((invitee, ind) => {
              const isHost = invitee.empCode === event.createdBy;

              return {
                value: invitee.empCode,
                label: (
                  <>
                    {invitee.name} {isHost && <strong>(Host)</strong>}
                  </>
                ),
              };
            }));
        setInvitees([...formatInvitees]);

        form.setFieldsValue({
          start: dayjs(event.start),
          end: dayjs(event.end),
          roomInvitees: formatInvitees,
          meetingRoomLink: event.meetingRoomLink,
        });
      })
      .catch((err) => {
        setInvitees([]);
      });
  }, [event]);

  useEffect(() => {
    const getEmployees = async () => {
      try {
        const resp = await staticApis.getEmployeesData(employeeId);
        const employeesData = resp?.data?.employeesData?.map((employee) => {
          return {
            label: employee.name + " ( " + employee.employeeNo + " ) ",
            value: employee.employeeNo,
          };
        });
        setEmployees([...employeesData]);
      } catch (err) {
        console.log("ERROR IN GET EMPLOYEES DATA API ", err);
      }
    };
    getEmployees();
  }, [])
  const copyToClipboard = (linkToCopy) => {
  navigator.clipboard.writeText(linkToCopy).then(() => {
    message.success("Link copied to clipboard");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  });
};


  const validateStartTime = (_, value) => {
    const endTime = form.getFieldValue("end").format("HH:mm");
    const formattedValue = value.format("HH:mm");

    if (value && endTime && formattedValue >= endTime) {
      return Promise.reject();
    }
    return Promise.resolve();
  };

  const isTimeUpdated = (event, data) => {
    if (
      event.start.getHours() === data.start.toDate().getHours() &&
      event.start.getMinutes() === data.start.toDate().getMinutes() &&
      event.end.getHours() === data.end.toDate().getHours() &&
      event.end.getMinutes() === data.end.toDate().getMinutes()
    ) {
      return false;
    }
    return true;
  };

  const isInviteesUpdated = (data, comparisonInvitees) => {
    if (data.roomInvitees.length !== comparisonInvitees.length) {
      return true;
    } else {
      if (
        data.roomInvitees.every((invitee) =>
          comparisonInvitees.includes(invitee)
        )
      ) {
        return false;
      }
      return true;
    }
  };

  const isLinkUpdated = (event, data) => {
    return event.meetingRoomLink !== data.meetingRoomLink;
  };

  const isFormUpdated = (data, comparisonInvitees) => {
    if (
      isTimeUpdated(event, data) ||
      isInviteesUpdated(data, comparisonInvitees) ||
      (event.meetingRoomLink && isLinkUpdated(event, data))
    ) {
      return true;
    }

    return false;
  };

  const handleSubmit = async (data) => {
    const { roomInvitees } = data;
    const comparisonInvitees = invitees.map((invitee) => invitee.value);
    if (typeof roomInvitees[0] === "object") {
      data.roomInvitees = roomInvitees.map((invitee) => invitee.value);
      if (!isFormUpdated(data, comparisonInvitees)) {
        toast.error("No changes made");
        handlePopupCancel();
        return;
      }
    } else {
      if (!isFormUpdated(data, comparisonInvitees)) {
        toast.error("No changes made");
        handlePopupCancel();
        return;
      }
    }

    // Compare invitees
    const areInviteesSame =
      data.roomInvitees.length === comparisonInvitees.length &&
      data.roomInvitees.every((val) => comparisonInvitees.includes(val));
    // Compare time
    const originalStart = dayjs(event.start).format("HH:mm");
    const originalEnd = dayjs(event.end).format("HH:mm");
    const updatedStart = data.start.format("HH:mm");
    const updatedEnd = data.end.format("HH:mm");
    const isTimeChanged =
      originalStart !== updatedStart || originalEnd !== updatedEnd;

    data.roomInvitees.push(event.empCode);
    const toastId = toast.loading("Loading...");
    setLoading(true);
    try {
      const meetingRoomPayload = {
        startTime: data.start.format("HH:mm"),
        endTime: data.end.format("HH:mm"),
        concurrencyStartDate: event.date.split("T")[0],
        concurrencyEndDate: event.date.split("T")[0],
        mrId: event.mrId,
        rtId: event.rtId,
      };

      const resp = await meetingRoomApis.getAvailabilityOfMeetingRoom(
        meetingRoomPayload
      );

      if (resp?.data?.noOfUnavailableSlots > 0) {
        toast.error("Meeting room not available");
      } else {
        const roomTimePayload = {
          startTime: data.start.format("HH:mm"),
          endTime: data.end.format("HH:mm"),
          roomTimeId: event.rtId,
          meetingRoomLink: event.mrId
            ? data.meetingRoomLink
            : event.meetingRoomLink.split("?jwt=")[0],
          roomInvitees: areInviteesSame ? null : data.roomInvitees,
          //roomInvitees: !areInviteesSame && isLinkUpdated(event, data) ? data.roomInvitees : null
        };

        if (!isTimeChanged && areInviteesSame && !isLinkUpdated(event, data)) {
          toast.error("No changes made");
          handlePopupCancel();
          return;
        }

        const inviteesPayload = {
          iviteesArray: data.roomInvitees,
        };

        if (
          isTimeChanged ||
          (event.meetingRoomName !== "Virtual Meeting" &&
            isLinkUpdated(event, data))
        ) {
          await roomBookingApi.updateRoomTime(roomTimePayload, {
            roomBookingId: event.rbId,
            empCode: event.empCode,
          });
        }
        if (!areInviteesSame) {
          await inviteesApi.updateInvitees(inviteesPayload, {
            id: event.rtId,
            empCode: event.empCode,
          });
        }

        handlePopupCancel();
        toast.success("Meeting updated successfully");
      }
    } catch (err) {
      console.log(
        "error in meeting room availability api in calendar popup",
        err
      );
      handlePopupCancel();
      toast.error("Error in meeting updation");
    }
    setLoading(false);
    toast.dismiss(toastId);
  };

  return (
    <>
      {/*For User */}

      <div id="meeting-heading" style={{ fontSize: "1.5em" }}>
        {dayjs(event.start).format("DD-MM-YYYY")}
      </div>

      {event.createdBy === event.empCode ? (
        event.date < dayjs(new Date()) ? (
          <>
            {canRelease && (
              <Flex justify="end" style={{ marginBottom: 8 }}>
                <Button danger onClick={openReleaseModal}>
                  Release room
                </Button>
              </Flex>
            )}
            <MeetingInformationPopup meetingData={event} invitees={invitees} />
          </>
        ) : (
          <>
            <div
              id="calendar-popup"
              className="popupDiv"
              // style={{overflow: "auto" }}
              style={{ maxHeight: "500px", overflowY: "auto" }}
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                style={{ position: "relative" }}
              >
                <Flex
                  gap={"0.5rem"}
                  style={{ marginTop: "5%" }}
                  vertical
                  justify="space-between"
                >
                  <Flex vertical>
                    <div className="meeting-title-heading">
                      {event.meetingRoomName !== "Virtual Meeting"
                        ? "Meeting Room"
                        : "Meeting Mode"}
                    </div>
                    <div className="meeting-title">{event.meetingRoomName}</div>
                  </Flex>
                  <Flex vertical>
                    <div className="meeting-title-heading">Subject</div>
                    <div className="meeting-title">{event.title}</div>
                  </Flex>
                  {event.agenda && (
                    <Flex vertical>
                      <div className="meeting-title-heading">Agenda</div>
                      <div
                        className="meeting-title"
                        dangerouslySetInnerHTML={{ __html: event.agenda }}
                      />
                    </Flex>
                  )}
                </Flex>
                {event.createdBy === event.empCode && (
                  <Flex
                    gap={"0.5rem"}
                    style={{
                      position: "absolute",
                      top: -10,
                      right: 0,
                      flexDirection: "row-reverse",
                    }}
                  >
                    <Button
                      type="primary"
                      htmlType="submit"
                      style={{ border: "none" }}
                      disabled={isPastMeeting || loading}
                    >
                      Edit
                    </Button>
                    {canRelease && (
                      <Button
                        danger
                        type="default"
                        onClick={openReleaseModal}
                        disabled={loading}
                      >
                        Release room
                      </Button>
                    )}
                    <Button
                      type="link"
                      danger
                      style={{
                        border: "none",
                        fontSize: "18px",
                        position: "relative",
                        top: -4,
                        right: -10,
                      }}
                      onClick={() =>
                        setConfirmationModal({
                          rtId: event.rtId,
                          rbId: event.rbId,
                          empCode: event.empCode,
                          concurrenceTypeId: event.concurrenceTypeId,
                        })
                      }
                      disabled={isPastMeeting || loading}
                    >
                      <DeleteOutlined />
                    </Button>
                  </Flex>
                )}
                {event.meetingRoomLink &&
                  (event.meetingRoomName === "Virtual Meeting" ? (
                    event.createdBy === event.empCode ? (
                      <>
                        <Form.Item label="Organizer Link">
                          <div className="virtual-meeting-container">
                            <a
                              href={event.meetingRoomLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="virtual-meeting-link"
                            >
                              Join Meeting
                            </a>

                            <Tooltip title="Copy link">
                              <CopyOutlined
                                onClick={() => copyToClipboard(event.meetingRoomLink)}
                                style={{
                                  cursor: "pointer",
                                  fontSize: "16px",
                                  color: "#1890ff",
                                }}
                              />
                            </Tooltip>
                          </div>
                        </Form.Item>
                        <Form.Item label="Attendee Link">
                          <div className="virtual-meeting-container">
                            <a
                              href={event.meetingRoomLink?.split("?")[0]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="virtual-meeting-link"
                            >
                              Join Meeting
                            </a>

                            <Tooltip title="Copy link">
                              <CopyOutlined
                                onClick={() =>
                                  copyToClipboard(
                                    event.meetingRoomLink?.split("?")[0]
                                  )
                                }
                                style={{
                                  cursor: "pointer",
                                  fontSize: "16px",
                                  color: "#1890ff",
                                }}
                              />
                            </Tooltip>
                          </div>
                        </Form.Item>
                      </>
                    ) : (
                      <Form.Item label="Link">
                        <div className="virtual-meeting-container">
                          <a
                            href={event.meetingRoomLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="virtual-meeting-link"
                          >
                            Join Meeting
                          </a>

                          <Tooltip title="Copy link">
                            <CopyOutlined
                              onClick={() => copyToClipboard(event.meetingRoomLink)}
                              style={{
                                cursor: "pointer",
                                fontSize: "16px",
                                color: "#1890ff",
                              }}
                            />
                          </Tooltip>
                        </div>
                      </Form.Item>
                    )
                  ) : (
                    <Form.Item
                      name="meetingRoomLink"
                      label="Link"
                      rules={[
                        {
                          type: "url",
                          message:
                            "Please enter a valid URL for the meeting room link",
                        },
                        {
                          validator: (_, value) =>
                            !value || value.startsWith("https://")
                              ? Promise.resolve()
                              : Promise.reject(
                                  new Error("URL must start with https://")
                                ),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        name="meetingRoomLink"
                        id="meetingRoomLink"
                      />
                    </Form.Item>
                  ))}
                <Form.Item
                  name="roomInvitees"
                  label="Invitees"
                  style={{
                    marginBottom: "1rem",
                    marginTop: "0.5rem",
                    fontSize: "0.8rem",
                    color: "grey",
                  }}
                  rules={[
                    // {
                    //   validator: (_, value) => {
                    //     if (event.meetingRoomName === "Virtual Meeting") {
                    //       if (!value || value.length === 0) {
                    //         return Promise.reject(
                    //           new Error("Invitees are required")
                    //         );
                    //       }
                    //     }
                    //     return Promise.resolve();
                    //   },
                    // },
                  ]}
                >
                  <Select
                    placeholder="Select invitees"
                    allowClear
                    onChange={(val) => {
                      form.setFieldValue("roomInvitees", val);
                    }}
                    filterOption={(input, employees) =>
                      (employees?.label ?? "")
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                    filterSort={(optionA, optionB) =>
                      (optionA?.label ?? "")
                        .toLowerCase()
                        .localeCompare((optionB?.label ?? "").toLowerCase())
                    }
                    options={employees}
                    mode="multiple"
                  />
                </Form.Item>

                <Flex
                  gap="0.5rem"
                  style={{
                    width: "100%",
                    marginBottom: "0.8rem",
                    alignItems: "start",
                  }}
                  align="center"
                >
                  <Form.Item
                    name="start"
                    label="Start Time"
                    rules={[
                      {
                        required: true,
                        message: "End time is required",
                      },
                      ...(event.meetingRoomName !== "Virtual Meeting"
                        ? [
                            {
                              validator: validateStartTime,
                              message: "Start time must be less than end time",
                            },
                          ]
                        : []),
                    ]}
                  >
                    <ClockTimePicker
                      size="large"
                      style={{ width: "100%" }}
                      placeholder="Select start time"
                      disabledTime={() => {
                        const starttime = dayjs();
                        const currentDate = dayjs().format("YYYY-MM-DD");
                        const currentHour = starttime.hour();
                        const currentMinute = starttime.minute();

                        return {
                          disabledHours: () => {
                            const hours = [];
                            if (event.date === currentDate) {
                              for (let i = 0; i < currentHour; i++)
                                hours.push(i);
                            }

                            return hours;
                          },
                          disabledMinutes: (selectedHour) => {
                            const minutes = [];
                            if (
                              selectedHour === currentHour &&
                              event.date === currentDate
                            ) {
                              for (let i = 0; i < currentMinute; i++)
                                minutes.push(i);
                            }
                            return minutes;
                          },
                        };
                      }}
                      onChange={(val) => {
                        form.setFieldValue("start", val);
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="end"
                    label="End Time"
                    initialValue={dayjs(event.end)}
                  >
                    <ClockTimePicker
                      size="large"
                      style={{ width: "100%" }}
                      placeholder="Select end time"
                      durationFrom={watchedStart}
                      //   disabledTime={
                      //     (time) => {
                      //     const starttime = dayjs();
                      //     const currentDate = dayjs().format("YYYY-MM-DD");
                      //     const currentHour = starttime.hour();
                      //     const currentMinute = starttime.minute();

                      //     return {
                      //       disabledHours: () => {
                      //         const hours = [];
                      //         if (event.date === currentDate) {
                      //           for (let i = 0; i < currentHour; i++)
                      //             hours.push(i);
                      //         }
                      //         return hours;
                      //       },
                      //       disabledMinutes: (selectedHour) => {
                      //         const minutes = [];
                      //         if (
                      //           selectedHour === currentHour &&
                      //           event.date === currentDate
                      //         ) {
                      //           for (let i = 0; i < currentMinute; i++)
                      //             minutes.push(i);
                      //         }
                      //         return minutes;
                      //       },
                      //     };
                      //   }
                      // }
                      onChange={(val) => {
                        form.setFieldValue("end", val);
                        form.validateFields(["start"]);
                      }}
                    />
                  </Form.Item>
                </Flex>
              </Form>
            </div>
          </>
        )
      ) : (
        <>
          {canRelease && (
            <Flex justify="end" style={{ marginBottom: 8 }}>
              <Button danger onClick={openReleaseModal}>
                Release room
              </Button>
            </Flex>
          )}
          <MeetingInformationPopup meetingData={event} invitees={invitees} />
        </>
      )}

      <Modal
        open={confirmationModal}
        onCancel={() => setConfirmationModal(false)}
        footer={null}
        style={{ position: "fixed", top: "145px", left: "35%" }}
      >
        <DeletePopup
          modalData={confirmationModal}
          setConfirmationModal={setConfirmationModal}
          handlePopupCancel={handlePopupCancel}
        />
      </Modal>
    </>
  );
};

export default CalendarPopup;
